// Feedback widget (PR #138, 2026-05-22). Floating button bottom-right,
// always visible. Click → small popover with textarea + Send button.
// Submit → POST /feedback → SendGrid email to operator with sender +
// page URL + note text. No DB persistence (dogfood scaffolding).

import { useEffect, useRef, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Check, MessageCircle, Send, X } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { ApiError, api } from "@/lib/api";
import { cn } from "@/lib/utils";

interface PostFeedbackResponse {
  email_send_id: string;
  recipient_email: string;
}

export function FeedbackWidget() {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [submittedAt, setSubmittedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const submit = useMutation({
    mutationFn: async (text: string) =>
      api<PostFeedbackResponse>("/feedback", {
        method: "POST",
        body: JSON.stringify({
          note_text: text,
          page_url: window.location.href,
          page_title: document.title,
        }),
      }),
    onError: (err) => {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setError(body?.error ?? `Send failed (${err.status}).`);
      } else {
        setError("Send failed. Try again or check your connection.");
      }
    },
    onSuccess: () => {
      setNote("");
      setSubmittedAt(Date.now());
      // Auto-close after the "sent" confirmation has been visible for
      // ~2 seconds. Reps get visual closure without manually clicking X.
      setTimeout(() => {
        setOpen(false);
        setSubmittedAt(null);
      }, 2000);
    },
  });

  // Esc closes when open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && !submit.isPending) {
        setOpen(false);
        setError(null);
        setSubmittedAt(null);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, submit.isPending]);

  // Focus the textarea when the popover opens.
  useEffect(() => {
    if (open && !submittedAt) textareaRef.current?.focus();
  }, [open, submittedAt]);

  // Click-outside closes (unless mid-submit).
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent): void => {
      if (submit.isPending) return;
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setError(null);
        setSubmittedAt(null);
      }
    };
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [open, submit.isPending]);

  const trimmed = note.trim();
  const canSubmit = trimmed.length > 0 && !submit.isPending && !submittedAt;

  return (
    <div
      ref={popoverRef}
      className="fixed bottom-6 right-6 z-40 print:hidden"
    >
      {open && (
        <div className="mb-3 w-80 rounded-lg border border-brown-100 bg-white shadow-xl">
          <div className="flex items-center justify-between border-b border-brown-50 px-4 py-2.5">
            <p className="font-display text-sm font-semibold text-brown-500">
              Send a note to Jake
            </p>
            <button
              type="button"
              onClick={() => {
                if (submit.isPending) return;
                setOpen(false);
                setError(null);
                setSubmittedAt(null);
              }}
              disabled={submit.isPending}
              className="text-brown-300 hover:text-brown-500 disabled:opacity-50"
              aria-label="Close feedback widget"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="px-4 py-3">
            {submittedAt ? (
              <div className="flex items-center gap-2 text-sm text-brown-500">
                <Check className="h-4 w-4 text-muted" />
                Sent. Thanks!
              </div>
            ) : (
              <>
                <textarea
                  ref={textareaRef}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={5}
                  maxLength={8000}
                  placeholder="What's broken, confusing, or missing on this page?"
                  className="w-full rounded-md border border-brown-100 bg-cream-50/40 px-3 py-2 text-sm text-brown-500 placeholder:text-[#8a8a85] focus:border-peach-300 focus:bg-white focus:outline-none focus:ring-2 focus:ring-peach-200"
                />
                <p className="mt-1 text-[11px] text-brown-300">
                  Includes the page URL + your name. Jake can reply
                  directly.
                </p>
                {error && (
                  <p className="mt-2 text-xs text-rust-600">{error}</p>
                )}
                <div className="mt-3 flex justify-end">
                  <Button
                    type="button"
                    size="sm"
                    disabled={!canSubmit}
                    onClick={() => {
                      setError(null);
                      submit.mutate(trimmed);
                    }}
                  >
                    <Send className="h-3.5 w-3.5" />{" "}
                    {submit.isPending ? "Sending…" : "Send"}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      <button
        type="button"
        onClick={() => {
          setOpen((prev) => !prev);
          setError(null);
          setSubmittedAt(null);
        }}
        aria-label={open ? "Close feedback" : "Send feedback to Jake"}
        title="Send a note to Jake about this page"
        className={cn(
          "flex h-12 w-12 items-center justify-center rounded-full shadow-lg transition-all",
          open
            ? "bg-brown-500 text-cream-200 hover:bg-brown-600"
            : "bg-peach-400 text-white hover:bg-peach-500 hover:scale-105",
        )}
      >
        {open ? (
          <X className="h-5 w-5" />
        ) : (
          <MessageCircle className="h-5 w-5" />
        )}
      </button>
    </div>
  );
}
